import { useCallback, useContext, useMemo } from 'react';
import { isEmpty } from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ActivationResponse,
  CustomSendEmailReset,
  CustomUser,
  CustomUserCreatePasswordRetype,
  JwtService,
  Oauth2Service,
  ProfilesService,
  SocialLoginRequest,
  UsersService,
} from 'api';
import { authAction, companiesAction, firmsAction, fundsAction, globalAction, usersAction } from 'common/actions';
import { signInPath } from 'common/config/urls';
import { AUTH_STORAGE_PERSISTENCE_LOCAL, IS_LOGGING_OUT_KEY, LOGGING_OUT_VALUE, SIGN_IN_VIEW } from 'common/constants/auth';
import type { AuthTokenPersistence } from 'common/constants/auth';
import { useStore } from 'common/store';
import { FIRM_LIST_STATUSES } from 'common/types/firm';
import type { UseStoreValues } from 'common/types/store';
import UnsavedChangesContext from 'context/UnsavedChangesContext';
import { clearPreviousWorkboardRoute } from 'dashboard409a/utilities/navigation';
import useDashboardReduxUserSync from 'pages/Auth/hooks/useDashboardReduxUserSync';
import {
  broadcastAuthLogout,
  buildSignInUrl,
  clearAppAutoSelect,
  clearEnterpriseSsoSignInSession,
  clearOtpVerified,
  clearPreviousPortfolioRoute,
  clearStoredAccessToken,
  clearStoredAuthTokens,
  getStoredAccessToken,
  getStoredAccessTokenUserId,
  getStoredRefreshToken,
  setStoredAuthTokens,
  singleFlightTokenRefresh,
} from 'pages/Auth/utils';
import LegacyUsersService from 'services/users';
import { resetZustandStores } from 'store/create';
import { Logger } from 'utilities';

interface AuthHookOptions {
  useGlobalLoading?: boolean;
}

type GlobalLoadingDispatch = (action: ReturnType<typeof globalAction.showLoadingProgress>) => void;
type ResetDashboardState = () => void;
type SyncDashboardUser = (userInfo: CustomUser) => void;
type StoreDispatch = UseStoreValues[1];

// Profile writes still depend on the legacy profiles endpoint until that contract is moved into the generated API surface.
const legacyUsersService = new LegacyUsersService();

const toggleGlobalLoading = (
  dispatch: GlobalLoadingDispatch,
  useGlobalLoading: boolean,
  isLoading: boolean
) => {
  if (useGlobalLoading) {
    dispatch(globalAction.showLoadingProgress(isLoading));
  }
};

const syncAuthenticatedUser = (
  dispatch: StoreDispatch,
  resetDashboardState: ResetDashboardState,
  syncDashboardUser: SyncDashboardUser,
  previousUserId: number | undefined,
  userInfo: CustomUser,
  callback?: () => void
) => {
  if (previousUserId !== userInfo.id) {
    resetDashboardState();
    resetZustandStores();
  }

  // Keep the legacy auth store and dashboard Redux user state in sync after auth changes.
  dispatch(authAction.setUserInfo(userInfo));
  syncDashboardUser(userInfo);
  callback?.();
};

const resetAuthenticatedUser = (
  dispatch: StoreDispatch,
  resetDashboardState: ResetDashboardState
) => {
  // Session loss must clear workspace authorization data too; returnTo can otherwise
  // mount a firm route with firms and permissions left by the previous session.
  dispatch(authAction.setUserInfo(null));
  dispatch(authAction.setIsRestrictedUser(null));
  dispatch(firmsAction.setFirmList(null));
  dispatch(firmsAction.setFirmListStatus(FIRM_LIST_STATUSES.IDLE));
  dispatch(firmsAction.setUserManagementFirmList(null));
  dispatch(firmsAction.setFirmId(null));
  dispatch(firmsAction.setFirmInfo(null));
  dispatch(companiesAction.setCompanyList(null));
  dispatch(fundsAction.setFundList(null));
  dispatch(usersAction.setOtherPermissions(null));
  resetDashboardState();
  resetZustandStores();
};

const persistAuthenticatedSession = ({
  accessToken,
  persistence = AUTH_STORAGE_PERSISTENCE_LOCAL,
  refreshToken,
}: {
  accessToken: string;
  persistence?: AuthTokenPersistence;
  refreshToken: string;
}) => {
  setStoredAuthTokens({
    accessToken,
    persistence,
    refreshToken,
  });
  // A fresh auth exchange must always resolve 2FA again unless the backend marks the session as verified.
  clearOtpVerified();
};

const clearExpiredSession = () => {
  clearStoredAuthTokens();
  clearOtpVerified();
};

const readUserProfile = async (userId: number): Promise<CustomUser> =>
  ProfilesService.apiProfilesRead(userId.toString());

export const useIsUserLoggedIn = () => {
  const [{ user }] = useStore();
  return useMemo(() => !isEmpty(user), [user]);
};

export const useLogOutUser = ({
  broadcastSessionLogout = true,
  noRedirect = false,
  redirectTo,
}: {
  broadcastSessionLogout?: boolean;
  noRedirect?: boolean;
  redirectTo?: string;
} = {}) => {
  const [{ user }, dispatch] = useStore() as UseStoreValues;
  const { resetDashboardState } = useDashboardReduxUserSync();
  const navigate = useNavigate();

  const logOutUser = useCallback(() => {
    // CustomRoute uses this flag to avoid a blank page while logout clears tokens and discovery state.
    localStorage.setItem(IS_LOGGING_OUT_KEY, LOGGING_OUT_VALUE);
    clearStoredAuthTokens();
    clearOtpVerified();
    clearEnterpriseSsoSignInSession();
    if (broadcastSessionLogout) {
      broadcastAuthLogout();
    }
    clearAppAutoSelect();
    // Explicit logout should not preserve resume routes into the next session or next user.
    clearPreviousPortfolioRoute(user?.id);
    clearPreviousWorkboardRoute(user?.id);

    resetAuthenticatedUser(dispatch, resetDashboardState);

    if (!noRedirect) {
      // Stores and tokens are already cleared above; SPA navigation avoids a full-document blank state.
      navigate(redirectTo ?? buildSignInUrl({ view: SIGN_IN_VIEW.IDENTIFIER }), { replace: true });
    }
  }, [broadcastSessionLogout, dispatch, navigate, noRedirect, redirectTo, resetDashboardState, user?.id]);

  return logOutUser;
};

export const useGetUserInfo = ({ useGlobalLoading = true }: AuthHookOptions = {}) => {
  const [{ user }, dispatch] = useStore() as UseStoreValues;
  const { resetDashboardState, syncDashboardUser } = useDashboardReduxUserSync();

  const getUserInfo = useCallback(
    async (callback?: () => void) => {
      try {
        const currentUserId = getStoredAccessTokenUserId();
        if (!currentUserId) {
          throw new Error('Unable to resolve current user id from access token.');
        }

        const userInfo = await readUserProfile(currentUserId);

        if (userInfo) {
          syncAuthenticatedUser(dispatch, resetDashboardState, syncDashboardUser, user?.id, userInfo, callback);
        }
      } catch (error) {
        Logger.error('useGetUserInfo', error);
        resetAuthenticatedUser(dispatch, resetDashboardState);
        // JWT verify can succeed while profile hydration fails; without clearing tokens,
        // useSignInRouteGuards retries verify in a loop and the sign-in shell stays on
        // "Checking your session." indefinitely.
        clearExpiredSession();
        clearEnterpriseSsoSignInSession();
      } finally {
        toggleGlobalLoading(dispatch, useGlobalLoading, false);
      }
    },
    [dispatch, resetDashboardState, syncDashboardUser, useGlobalLoading, user?.id]
  );

  return getUserInfo;
};

export const useUpdateUserProfileInfo = ({ useGlobalLoading = true }: AuthHookOptions = {}) => {
  const [, dispatch] = useStore();

  return async (userProfileInfo: Partial<CustomUser>): Promise<[CustomUser | null, unknown]> => {
    try {
      toggleGlobalLoading(dispatch, useGlobalLoading, true);
      const currentUserId = getStoredAccessTokenUserId();
      if (!currentUserId) {
        throw new Error('Unable to resolve current user id from access token.');
      }

      // Terms and profile edits must use /api/profiles/{id}; /auth/users/{id} is read-oriented in the current backend.
      const data = await legacyUsersService.updateUserProfile(currentUserId, userProfileInfo);
      return [data, null];
    } catch (error) {
      return [null, error];
    } finally {
      toggleGlobalLoading(dispatch, useGlobalLoading, false);
    }
  };
};

export const useSendPasswordReset = ({ useGlobalLoading = true }: AuthHookOptions = {}) => {
  const [, dispatch] = useStore();

  const sendPasswordReset = async (email: string) => {
    if (!email) {
      return undefined;
    }

    try {
      toggleGlobalLoading(dispatch, useGlobalLoading, true);
      const response = await UsersService.authUsersResetPassword({ email });
      return response;
    } catch (error) {
      return error;
    } finally {
      toggleGlobalLoading(dispatch, useGlobalLoading, false);
    }
  };

  return sendPasswordReset;
};

export const useSendPasswordChange = () => {
  const sendPasswordChange = async (newPassword: string, reNewPassword: string, uid: string, token: string) => {
    if (!newPassword || !reNewPassword) {
      return undefined;
    }

    try {
      const response = await UsersService.authUsersResetPasswordConfirm({
        new_password: newPassword,
        re_new_password: reNewPassword,
        uid,
        token,
      });
      return response;
    } catch (error) {
      return error;
    }
  };

  return sendPasswordChange;
};

export const useGetAccessTokens = ({ useGlobalLoading = true }: AuthHookOptions = {}) => {
  const [, dispatch] = useStore();
  const getUserInfo = useGetUserInfo({ useGlobalLoading });

  const getAccessTokens = async (
    username: string,
    password: string,
    callback: () => void,
    persistence: AuthTokenPersistence = AUTH_STORAGE_PERSISTENCE_LOCAL
  ) => {
    if (!username || !password) {
      return undefined;
    }

    try {
      toggleGlobalLoading(dispatch, useGlobalLoading, true);
      const accessTokens = await JwtService.authJwtCreateCreate({
        username,
        password,
      });

      if (accessTokens) {
        persistAuthenticatedSession({
          accessToken: accessTokens.access,
          persistence,
          refreshToken: accessTokens.refresh,
        });

        await getUserInfo(callback);

        return accessTokens;
      }
    } catch (error) {
      return error;
    } finally {
      toggleGlobalLoading(dispatch, useGlobalLoading, false);
    }

    return undefined;
  };

  return getAccessTokens;
};

export const useRefreshAccessToken = ({ useGlobalLoading = true }: AuthHookOptions = {}) => {
  const [, dispatch] = useStore();
  const getUserInfo = useGetUserInfo({ useGlobalLoading });
  const location = useLocation();
  const navigate = useNavigate();
  const { setAction } = useContext(UnsavedChangesContext);
  const { resetDashboardState } = useDashboardReduxUserSync();

  const cleanUserInfo = useCallback(
    (preserveReturnTo = false) => {
      setAction(false);
      resetAuthenticatedUser(dispatch, resetDashboardState);
      navigate(
        // returnTo is only preserved for involuntary session loss, not voluntary sign out.
        preserveReturnTo
          ? buildSignInUrl({
            returnTo: `${location.pathname}${location.search}`,
            view: SIGN_IN_VIEW.IDENTIFIER,
          })
          : signInPath
      );
    },
    [dispatch, location.pathname, location.search, navigate, resetDashboardState, setAction]
  );

  const refreshAccessToken = useCallback(async () => {
    const refreshToken = getStoredRefreshToken();

    if (!refreshToken) {
      clearExpiredSession();
      cleanUserInfo();
      return undefined;
    }

    try {
      const newAccess = await singleFlightTokenRefresh();

      if (newAccess) {
        await getUserInfo();
        return { access: newAccess };
      }

      cleanUserInfo(true);
    } catch (error) {
      Logger.error('useRefreshAccessToken', error);
      cleanUserInfo(true);
      clearExpiredSession();
    } finally {
      toggleGlobalLoading(dispatch, useGlobalLoading, false);
    }

    return undefined;
  }, [getUserInfo, dispatch, cleanUserInfo, useGlobalLoading]);

  return refreshAccessToken;
};

export const useVerifyAccessToken = ({ useGlobalLoading = true }: AuthHookOptions = {}) => {
  const [, dispatch] = useStore();
  const getUserInfo = useGetUserInfo({ useGlobalLoading });
  const refreshToken = useRefreshAccessToken({ useGlobalLoading });

  const verifyAccessToken = useCallback(async () => {
    const accessToken = getStoredAccessToken();
    if (accessToken?.length) {
      try {
        const response = await JwtService.authJwtVerifyCreate({
          token: accessToken,
        });

        if (response) {
          await getUserInfo();
        }
      } catch (error) {
        Logger.error('useVerifyAccessToken', error);
        // Verification failures should retry from the refresh token, never from the stale access token
        // that already failed backend verification.
        clearStoredAccessToken();
        try {
          await refreshToken();
        } catch (refreshError: unknown) {
          throw new Error(String(refreshError));
        }
      } finally {
        toggleGlobalLoading(dispatch, useGlobalLoading, false);
      }
    }
  }, [getUserInfo, dispatch, refreshToken, useGlobalLoading]);

  return verifyAccessToken;
};

export const useCreateUser = ({ useGlobalLoading = true }: AuthHookOptions = {}) => {
  const [, dispatch] = useStore();

  const createUser = async (
    newUserData: CustomUserCreatePasswordRetype
  ): Promise<[CustomUserCreatePasswordRetype | null, unknown]> => {
    try {
      toggleGlobalLoading(dispatch, useGlobalLoading, true);
      const data = await UsersService.authUsersCreate(newUserData);
      return [data, null];
    } catch (error) {
      return [null, error];
    } finally {
      toggleGlobalLoading(dispatch, useGlobalLoading, false);
    }
  };

  return createUser;
};

export const useActivateUser = ({ useGlobalLoading = true }: AuthHookOptions = {}) => {
  const [, dispatch] = useStore();

  const activateUser = useCallback(
    async (uid: string, token: string): Promise<[ActivationResponse | null, unknown]> => {
      try {
        toggleGlobalLoading(dispatch, useGlobalLoading, true);
        const data = await UsersService.authUsersActivation({ uid, token });
        return [data, null];
      } catch (error) {
        return [null, error];
      } finally {
        toggleGlobalLoading(dispatch, useGlobalLoading, false);
      }
    },
    [dispatch, useGlobalLoading]
  );

  return activateUser;
};

export const useResendActivationEmail = ({ useGlobalLoading = true }: AuthHookOptions = {}) => {
  const [, dispatch] = useStore();

  const resendActivationEmail = async (email: string): Promise<[CustomSendEmailReset | null, unknown]> => {
    try {
      toggleGlobalLoading(dispatch, useGlobalLoading, true);
      const data = await UsersService.authUsersResendActivation({ email });
      return [data, null];
    } catch (error) {
      return [null, error];
    } finally {
      toggleGlobalLoading(dispatch, useGlobalLoading, false);
    }
  };

  return resendActivationEmail;
};

export const useGetSocialAccessToken = ({ useGlobalLoading = true }: AuthHookOptions = {}) => {
  const [, dispatch] = useStore();
  const getUserInfo = useGetUserInfo({ useGlobalLoading });

  return async (
    payload: SocialLoginRequest,
    persistence: AuthTokenPersistence = AUTH_STORAGE_PERSISTENCE_LOCAL
  ) => {
    if (!payload) {
      return undefined;
    }

    try {
      toggleGlobalLoading(dispatch, useGlobalLoading, true);
      const response = await Oauth2Service.apiOauth2SocialCreate(payload);

      if (response) {
        persistAuthenticatedSession({
          accessToken: response.access,
          persistence,
          refreshToken: response.refresh,
        });
        dispatch(authAction.setUserInfo(response.user));

        await getUserInfo();
      }
    } catch (error) {
      return error;
    } finally {
      toggleGlobalLoading(dispatch, useGlobalLoading, false);
    }

    return undefined;
  };
};
